import { useEffect, useState, type FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { TopBarNotificationType } from 'constants/notifications';
import { ThemeName } from 'constants/themes';
import { ToastContent } from 'constants/toasts';
import { isMobileViewMemoized } from 'helpers/device';
import { openInCard } from 'helpers/routing';
import { showToast } from 'helpers/toast';
import { useAgentCustomProperty } from 'hooks/use-agent-custom-property';
import { useWindowSize } from 'hooks/use-window-size';
import { AgentCustomPropertyName } from 'store/features/agent-custom-properties/interfaces';
import { getCurrentTheme, hasFetchedAgentCustomProperties } from 'store/features/agent-custom-properties/selectors';
import { getOpenedGlobalModal } from 'store/features/global-modals/selectors';
import { NotificationsBarActions } from 'store/features/notifications-bar/actions';
import { ToastVariant } from 'store/features/toasts/interfaces';

import { LEGACY_MIGRATION_DISMISS_DURATION_MS, LEGACY_MIGRATION_MODAL_ASSETS } from './constants';
import { getAiAgentActivationUrl, isLegacyMigrationSurfaceDismissed } from './helpers';
import { LegacyMigrationModal } from './LegacyMigrationModal';
import { useLegacyMigrationEligibility } from './use-legacy-migration-eligibility';

export const LegacyMigrationCampaign: FC = () => {
  const dispatch = useDispatch();
  const { width: windowWidth } = useWindowSize();
  const { isEligible, isFetched: isEligibilityFetched } = useLegacyMigrationEligibility();
  const areAgentPropertiesFetched = useSelector(hasFetchedAgentCustomProperties);
  const currentTheme = useSelector(getCurrentTheme);
  const openedGlobalModal = useSelector(getOpenedGlobalModal);
  const [isModalDismissedInSession, setIsModalDismissedInSession] = useState(false);
  const [modalDismissedLocallyUntil, setModalDismissedLocallyUntil] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now);
  const [modalDismissedUntil, setModalDismissedUntil] = useAgentCustomProperty<number>(
    AgentCustomPropertyName.LegacyMigrationModalDismissedUntil,
    0,
  );
  const [isModalPermanentlyDismissed, setModalPermanentlyDismissed] = useAgentCustomProperty<boolean>(
    AgentCustomPropertyName.LegacyMigrationModalPermanentlyDismissed,
    false,
  );
  const [topbarDismissedUntil] = useAgentCustomProperty<number>(
    AgentCustomPropertyName.LegacyMigrationTopbarDismissedUntil,
    0,
  );
  const [isTopbarPermanentlyDismissed] = useAgentCustomProperty<boolean>(
    AgentCustomPropertyName.LegacyMigrationTopbarPermanentlyDismissed,
    false,
  );

  const isReady = isEligibilityFetched && areAgentPropertiesFetched;
  const shouldShowCampaign = isReady && isEligible && !isMobileViewMemoized(windowWidth);
  const effectiveModalDismissedUntil = Math.max(modalDismissedUntil, modalDismissedLocallyUntil);
  const shouldShowModal =
    shouldShowCampaign &&
    !openedGlobalModal &&
    !isModalDismissedInSession &&
    !isLegacyMigrationSurfaceDismissed(isModalPermanentlyDismissed, effectiveModalDismissedUntil, currentTime);
  const shouldShowTopbar =
    shouldShowCampaign &&
    !isLegacyMigrationSurfaceDismissed(isTopbarPermanentlyDismissed, topbarDismissedUntil, currentTime);

  useEffect(() => {
    const nextExpiry = [effectiveModalDismissedUntil, topbarDismissedUntil]
      .filter((dismissedUntil) => dismissedUntil > currentTime)
      .sort((first, second) => first - second)[0];

    if (!nextExpiry) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setCurrentTime(nextExpiry), Math.max(0, nextExpiry - Date.now()));

    return () => window.clearTimeout(timeoutId);
  }, [currentTime, effectiveModalDismissedUntil, topbarDismissedUntil]);

  useEffect(() => {
    const action = shouldShowTopbar
      ? NotificationsBarActions.showNotificationsBar({ name: TopBarNotificationType.LegacyMigration })
      : NotificationsBarActions.hideNotificationsBar(TopBarNotificationType.LegacyMigration);

    dispatch(action);
  }, [dispatch, shouldShowTopbar]);

  useEffect(
    () => () => {
      dispatch(NotificationsBarActions.hideNotificationsBar(TopBarNotificationType.LegacyMigration));
    },
    [dispatch],
  );

  if (!shouldShowModal) {
    return null;
  }

  const dismissModalInSession = (): void => {
    setIsModalDismissedInSession(true);
  };

  const handleRemindMeLater = (): void => {
    const dismissedUntil = Date.now() + LEGACY_MIGRATION_DISMISS_DURATION_MS;

    setModalDismissedUntil(dismissedUntil);
    setModalDismissedLocallyUntil(dismissedUntil);
  };

  const handlePrimaryAction = (): void => {
    setModalPermanentlyDismissed(true, {
      onSuccess: () => {
        setIsModalDismissedInSession(true);
        openInCard(getAiAgentActivationUrl());
      },
      onFailure: () => showToast(ToastVariant.Error, ToastContent.AGENT_CUSTOM_PROPERTY_UPDATE_FAILURE),
    });
  };

  const imageSource =
    currentTheme === ThemeName.Dark ? LEGACY_MIGRATION_MODAL_ASSETS.dark : LEGACY_MIGRATION_MODAL_ASSETS.light;

  return (
    <LegacyMigrationModal
      imageSource={imageSource}
      onClose={dismissModalInSession}
      onRemindMeLater={handleRemindMeLater}
      onPrimaryAction={handlePrimaryAction}
    />
  );
};
